import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { FilterPopoverHeader } from '@/components/listings/FilterPopoverHeader';

export interface MultiSelectOption {
  value: string;
  label: ReactNode;
  count?: number;
}

export interface MultiSelectGroup {
  label?: string;
  muted?: boolean;
  options: MultiSelectOption[];
}

export interface MultiSelectDraft {
  draft: string[];
  setDraft: (next: string[] | ((prev: string[]) => string[])) => void;
  toggle: (value: string) => void;
}

interface Props {
  label: ReactNode;
  icon?: ReactNode;
  value: string[];
  /** Fires once on close/Apply; `undefined` means nothing selected. */
  onChange: (next: string[] | undefined) => void;
  /** Standard checkbox list. Use `children` instead for custom rows (e.g. tri-state groups). */
  groups?: MultiSelectGroup[];
  children?: (ctx: MultiSelectDraft) => ReactNode;
  disabled?: boolean;
  align?: 'start' | 'end';
  contentClassName?: string;
  triggerClassName?: string;
  active?: boolean;
}

/**
 * The one multi-select dropdown template: draft selection, pinned Clear/Apply
 * header, capped scroll body, count badge. Closing applies, like Apply.
 */
export function MultiSelectFilter({
  label, icon, value, onChange, groups, children, disabled,
  align = 'start', contentClassName, triggerClassName, active,
}: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);

  const handleOpen = (o: boolean) => {
    if (o) setDraft(value);
    else onChange(draft.length ? draft : undefined);
    setOpen(o);
  };
  const toggle = (v: string) =>
    setDraft((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  const isActive = active ?? value.length > 0;

  return (
    <Popover modal open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn('h-9 gap-1.5 text-sm font-medium', triggerClassName)}
          data-active={isActive || undefined}
        >
          {icon}
          <span className="min-w-0 truncate">{label}</span>
          {value.length > 0 && (
            <Badge variant="secondary" className="h-5 min-w-5 shrink-0 px-1.5 text-xs">{value.length}</Badge>
          )}
          <ChevronDown className="ml-auto h-3 w-3 shrink-0" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className={cn(
          'w-56 p-0 overlay-scroll max-h-[min(420px,var(--radix-popover-content-available-height))]',
          contentClassName,
        )}
      >
        <FilterPopoverHeader count={draft.length} onClear={() => setDraft([])} onApply={() => handleOpen(false)} />
        <div className="p-2">
          {children
            ? children({ draft, setDraft, toggle })
            : groups?.map((g, i) => (
                <div key={g.label ?? i} className={cn(i > 0 && 'mt-2 pt-2 border-t border-border')}>
                  {g.label && (
                    <h4 className={cn('px-2 mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground', g.muted && 'italic opacity-60')}>
                      {g.label}
                    </h4>
                  )}
                  {g.options.map((o) => (
                    <label
                      key={o.value}
                      className={cn('flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm', g.muted && 'italic text-muted-foreground')}
                    >
                      <Checkbox checked={draft.includes(o.value)} onCheckedChange={() => toggle(o.value)} disabled={disabled} />
                      <span className="flex-1">{o.label}</span>
                      {o.count !== undefined && <span className="text-xs text-muted-foreground num">{o.count}</span>}
                    </label>
                  ))}
                </div>
              ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
